import { PackageProviderName } from 'data/ppoints/constants';
import { useMemo } from 'react';
import useSWR from 'swr';
import swrConfig from 'utils/swrConfig';
import {
    AnalyticsOrderItem,
    AnalyticsOrderResponse,
} from '../../../../../../../../pb/responses/v1/analytics_order_response_pb';
import { getProviderOrderAnalytics, PROVIDER_ORDER_ANALYTICS_PATH } from '../api/providerOrderAnalyticsApi';

const LAST_DAYS = 30;

export interface MostUsedProvider {
    /** The provider with the most orders in the last 30 days, undefined when there are none */
    provider?: PackageProviderName;
    /** That provider's order count */
    count: number;
    /** Every provider's orders together */
    total: number;
    isLoading: boolean;
    error?: Error;
}

/** The most used provider of the last 30 days, from the per-provider order analytics */
export const useMostUsedProvider = (): MostUsedProvider => {
    // Fixed for the component's lifetime: the date is part of the SWR key
    const from = useMemo(() => {
        const date = new Date();
        date.setDate(date.getDate() - LAST_DAYS);
        date.setHours(0, 0, 0, 0);
        return date;
    }, []);

    const { data, isLoading, error } = useSWR<AnalyticsOrderResponse>(
        [PROVIDER_ORDER_ANALYTICS_PATH, from.toISOString()],
        () => getProviderOrderAnalytics(from),
        swrConfig
    );

    return useMemo(() => {
        const items = data?.items ?? [];
        const total = items.reduce((sum, item) => sum + item.count, 0);
        const mostUsed = items.reduce<AnalyticsOrderItem | undefined>(
            (best, item) => (item.count > (best?.count ?? 0) ? item : best),
            undefined
        );

        return {
            provider: mostUsed?.name as PackageProviderName | undefined,
            count: mostUsed?.count ?? 0,
            total,
            isLoading,
            error,
        };
    }, [data, isLoading, error]);
};
